/*
Copyright 2022 Alexandra Instituttet

Der er givet en begrænset licens af denne software til GEO. Licensen
giver dem ret til at benytte, videreudvikle og fejlrette udelukkende
med henblik på brug i GeoAtlasStudy web-applikationen.
*/
/* eslint-disable no-unused-vars, eqeqeq */

// External ////////////////////////////////////////////////////////
import { mat3, mat4, vec2, vec3, vec4 } from 'gl-matrix';

// wfundament ////////////////////////////////////////////////////////////
import {
    WBox,
    WCylinder,
    WFrustum,
    WImageLoader,
    WKeyboardEventHandler,
    WLruCache,
    WMath,
    wMathConst,
    wMouseButtons,
    WMouseEventHandler,
    WOrbitCamera,
    WSphere,
    WTextLayout,
    WTouchEventHandler,
    wVec4Colors,
} from 'wfundament';
import { wClipPlaneAxis } from 'wfundament/source/wuxi/WClipPlane.js';
import { WUxi } from 'wfundament/source/wuxi/WUxi.js';

// wgtek ///////////////////////////////////////////////////////////
import {
    WgBuffer,
    WgContext,
    WgFbo,
    WgFontRender,
    WgPrimRender,
    WgResourceCache,
    WgShader,
    WgTexture,
    WgVertexBuffer,
    WgVertexDescriptor,
    WgWuxiRender,
} from 'wgtek';

// webasm //////////////////////////////////////////////////////////
import nrrd from 'nrrd-js';
import { VoxelModel } from './voxel/VoxelModel.js';
////////////////////////////////////////////////////////////////////////
import { ClipPolygon } from './ClipPolygon.js';
import { VoxelTransferFunction } from './voxel/VoxelTransferFunction.js';

import { W3DTileSet } from './tile3d/W3DTileSet.js';
import { W3DTile } from './tile3d/W3DTile.js';

// webasm //////////////////////////////////////////////////////////
/* eslint-disable import/no-webpack-loader-syntax */
import Worker from 'worker-loader!./tile3d/TileLoader.worker.js';
import aarhus_nrrd from '!!file-loader!../assets/aarhus_compress.nrrd';
import voxelDefinition from '!!file-loader!../assets/VoxelDefinition.json.txt'; //
import verdana_json from '!!file-loader!../assets/verdana.json.txt'; //
//import verdana_png     from '!!file-loader!../assets/verdana.png';
//const verdana_json = "assets/verdana.json"; //
import verdana_png from "!!file-loader!../assets/verdana.png";

const tilesetPath = process.env.PUBLIC_URL + '/boreholes/';
const buildingsTilesetPath = process.env.PUBLIC_URL + '/citymodel/';
//const tilesetPath = "assets/boreholes/";
//const buildingsTilesetPath = "assets/aarhus/";
const gFontTextureName = "FontTexture";
const DISABLE_PICKING = false;
const kftoken_ = "fdf320eb064dce1c5db925a2d7f32783";
let aarhus_overlay_image_url = `https://api.dataforsyningen.dk/topo_skaermkort_DAF?SERVICE=WMS&VERSION=1.1.1&REQUEST=GetMap&FORMAT=image/png&TRANSPARENT=TRUE&SERVICENAME=topo_skaermkort_DAF&LAYERS=dtk_skaermkort_graa&TOKEN=${kftoken_}&STYLES=&WIDTH=1024&HEIGHT=1024&SRS=EPSG:25832&BBOX=`

// UI EVENTS ///////////////////////////////////////////////////////////
let buttonEvent_ = false;
let toggleDrawClipPlaneEvent_ = false;
let currentClipPlaneAxis_ = 0;

// ui ids
const hasPassive = false;
const ToggleClipPlaneDrawButtonId = 2;
const ToggleAxisAlignedClipPlaneId = 3;
const ToggleClipPlaneLabelId = 4;
const AxisClipPlaneLabelId = 5;
const AxisClipPlaneToggleAxisLabelId = 6;
const ClipPlaneFrameId = 10;

const PickingInfoFrameId = 20;
const PickingIdLabel_ = 21;
const PickingNameLabel_ = 22;
const PickingNameStr_ = "Selected voxel name: ";
const PickingIdStr_ = "Selected voxel id: ";

const clipPlanePosXIndex = 64;
const clipPlanePosZIndex = 65;

const drawHeight = 1;

let PerformPickingPass = false;

///////////////////////////////////////////////////////////////////////
function buttonCallBack(id) {
    //console.log("button id: " + id + " clicked");

    if (id === 0) {
        buttonEvent_ = true;
    }

    if (id === ToggleAxisAlignedClipPlaneId) {
        currentClipPlaneAxis_++;
        if (currentClipPlaneAxis_ > 2) {
            currentClipPlaneAxis_ = 0;
        }
    }

    if (id === ToggleClipPlaneDrawButtonId) {
        toggleDrawClipPlaneEvent_ = true;
    }
}

const buildingsVsSrc = `#version 300 es
precision mediump float;
in vec4 Pos;
in vec3 Normal;
out vec3 n;
uniform mat4 VP;

void main()
{
n = Normal;
gl_Position = VP * Pos;
}`;
const buildingsFsSrc = `#version 300 es
precision mediump float;
in vec3 n;
out vec4 fragData;
uniform float transparency;

void main()
{
vec3 lightDir = vec3(1.0)/1.73;
fragData = vec4(vec3(clamp(dot(n, lightDir),0.0,1.0)*0.8 +0.2) , 1.0-transparency);
}
`;

const boreholeVsSrc = `#version 300 es
precision mediump float;
in vec4 Pos;
in vec3 Normal;
in vec3 Color;
out vec3 n;
out vec3 col;
uniform mat4 VP;

void main()
{
col = Color;
n = Normal;
gl_Position = VP * Pos;
}`;

const boreholeFsSrc = `#version 300 es
precision mediump float;
in vec3 n;
in vec3 col;
out vec4 fragData;
uniform float transparency;

void main()
{
vec3 lightDir = vec3(1.0)/1.73;
fragData = vec4(col*vec3(clamp(dot(n, lightDir),0.0,1.0)*0.4 +0.6) , 1.0-transparency);
}
`;

const boreholeIdVsSrc = `#version 300 es
precision mediump float;
in vec4 Pos;
in highp float BatchId;
out highp float id;
uniform mat4 VP;

void main()
{
id = BatchId;
gl_Position = VP * Pos;
}`;

const boreholeIdFsSrc = `#version 300 es
precision mediump float;
in highp float id;
out vec4 fragData;

void main()
{
uint uid = uint(id);
fragData.x = float((uid>>24u) & 0xFFu)/255.0;
fragData.y = float((uid>>16u) & 0xFFu)/255.0;
fragData.z = float((uid>>8u) & 0xFFu)/255.0;
fragData.w = float(uid & 0xFFu)/255.0;
}
`;

////////////////////////////////////////////////////////////////////

export const AppMode = {
    AppNormal: 0,
    AppDrawClipPlane: 1,
    AppTopView: 2,
};

// app /////////////////////////////////////////////////////////////
export class WgApp {
    constructor(canvas) {
        const gl = canvas.getContext("webgl2", { preserveDrawingBuffer: true });

        this.glContext_ = new WgContext(gl, canvas);
        this.gl = gl;
        this.frameCount_ = 0;
        this.lastFps_ = 0;
        this.resourceCache_ = new WgResourceCache(); // a convinient way to share
        // textures and other graphics
        // resources.

        this.appMode_ = AppMode.AppNormal;
        this.voxelLightValue_ = 1.0;

        this.cameraPosition_ = vec3.fromValues(0, 4, 25.6 - 15);
        this.orbitCamera_ = new WOrbitCamera("orbitCamera");
        this.orbitCamera_.nearPlane_ = 0.01;
        this.orbitCamera_.setViewport(
            this.glContext_.getCanvasWidth(),
            this.glContext_.getCanvasHeight()
        );
        this.orbitCamera_.setOrbitPivotTarget([25.6, 25.6, 0.0]);
        this.orbitCamera_.setPerspective(60.0);
        this.orbitCamera_.setUpvector(vec3.fromValues(0.0, 0.0, 1.0));
        this.orbitCamera_.setZoomSpeed(3.0);
        this.orbitCamera_.setCameraPosition(this.cameraPosition_);
        this.cameraActiveCounter_ = 0; // used to make sure that the active camera
        // keeps the focus

        this.frustum_ = new WFrustum();
        this.frustum_.updateFromViewProjection(
            this.orbitCamera_.getViewProjection()
        );
        this.frustum_.updateFrustumPoints(this.orbitCamera_.getViewProjection());

        this.lruCache_ = [new WLruCache(64), new WLruCache(96)];
        this.visibleTiles_ = [[], []];
        this.tilesMaxDistance_ = 10.0;

        this.buildingsShader_ = new WgShader("shader", this.glContext_);
        this.buildingsShader_.createShaderProgram(buildingsVsSrc, buildingsFsSrc, {
            Pos: 0,
            Normal: 1,
        });

        this.boreholeShader_ = new WgShader("boreholeshader", this.glContext_);
        this.boreholeShader_.createShaderProgram(boreholeVsSrc, boreholeFsSrc, {
            Pos: 0,
            Normal: 1,
            Color: 2,
        });

        this.boreholeIdShader_ = new WgShader("idshader", this.glContext_);
        this.boreholeIdShader_.createShaderProgram(
            boreholeIdVsSrc,
            boreholeIdFsSrc,
            { Pos: 0, BatchId: 3 }
        );

        this.showTileset_ = [true, true];

        // Font rendering
        this.fontRender_ = null;
        this.primRender_ = null;
        this.wuxiRender_ = null;
        this.wuxi_ = null;

        //event handlers
        this.keyboard_handler_ = new WKeyboardEventHandler();
        this.mouseEventHander_ = new WMouseEventHandler();
        this.touchEventHandler_ = new WTouchEventHandler();
        this.image_loader_ = new WImageLoader("image_loader");

        // dat gui
        this.browerInfo_ = {
            browser: "unknown",
            winner: true,
        };

        this.slider_ = {
            name: "Slider",
            value: 0.0,
            maxSize: 1.0,
        };

        this.appInfo_ = {
            appName: "GeoNovo project 3D",
            appDeveloper: "Trier & Kjeldsen",
            fps: 0,
            visibleTilesBorehole: 0,
            visibleTilesBuildings: 0,
        };

        this.domainSize_ = [1, 1];
        this.domainOrigin_ = [0, 0, 0];
        this.geoTransform_ = mat4.create();
        this.heightScale_ = 1.0;

        this.pickingRay_ = null;
        /*
         this.debugGui_ = new dat.GUI({ name: 'Debug input' });
         this.debugGui_.add(this.appInfo_, 'appName');
         let fpsController = this.debugGui_.add(this.appInfo_, 'fps');
         let cullController = this.debugGui_.add(this.appInfo_, 'visibleTilesBorehole');
         let cullBuildingsController = this.debugGui_.add(this.appInfo_, 'visibleTilesBuildings');
         this.debugGui_.add(this.slider_, "value").min(0.0).max(1.0);


         setInterval(() => {
         fpsController.setValue(this.appInfo_.fps);
         cullController.setValue(this.appInfo_.visibleTilesBorehole);
         cullBuildingsController.setValue(this.appInfo_.visibleTilesBuildings);
         }, 1000);
         */

        this.clipLine_ = [];
        this.currentAxisClipPlane_ = -1;

        this.clipPolygon_ = new ClipPolygon();
        this.clipPolygon_.setClosePolygonRadius(1.0);
        this.boxIsecPoint_ = vec3.create();

        this.scheduleRender_ = true;
        this.tileset_ = [];
        //this.tileLoaderWorker_ = new Worker(new URL('./TileLoader.worker.js',
        // import.meta.url));
        this.tileLoaderWorker_ = new Worker();

        // Set hander when tile data is loaded in worker
        this.tileLoaderWorker_.onmessage = ({ data }) => {
            const tile = data.tile;
            if (!this.lruCache_[tile.tilesetIndex_].inCache(tile.json_.content.uri)) {
                data = null;
                return;
            }

            tile.vertexBuffer_ = new WgVertexBuffer(
                `vbo${tile.dataUrl_}`,
                this.glContext_
            );

            const gl = this.glContext_.gl();
            let desc = new WgVertexDescriptor(tile.vertexType_, this.glContext_.gl());

            tile.vertexBuffer_.createVertexBuffer(
                gl.STATIC_DRAW,
                desc.vertexLayout_,
                tile.vertexData_.length /
                (desc.getVertexSizeInBytes() / Float32Array.BYTES_PER_ELEMENT),
                desc.getVertexSizeInBytes(),
                tile.vertexData_
            );
            tile.vertexData_ = null;
            tile.indexBuffer_ = null;
            if (tile.indexData_) {
                tile.indexBuffer_ = new WgBuffer("testIndexbuffer", this.glContext_);
                tile.indexBuffer_.createBuffer(
                    gl.ELEMENT_ARRAY_BUFFER,
                    gl.STATIC_DRAW,
                    tile.indexData_.length,
                    tile.indexData_.BYTES_PER_ELEMENT,
                    tile.indexData_
                );

                tile.vertexBuffer_.bind();
                tile.indexBuffer_.bind();
                tile.vertexBuffer_.unbind();
                tile.indexBuffer_.unbind();
                tile.indexData_ = null;
            }

            // Insert the real tile in the cache
            if (this.lruCache_[tile.tilesetIndex_].inCache(tile.json_.content.uri)) {
                this.lruCache_[tile.tilesetIndex_].remove(tile.json_.content.uri);
                this.lruCache_[tile.tilesetIndex_].insert(tile.json_.content.uri, tile);

                tile.getName = function () {
                    return `${tile.dataUrl_}`;
                }; // Set method
                // for
                // resource
                // cache
                this.resourceCache_.addResource(tile);
            }

            this.scheduleRender_ = true;
        };

        this.lruCache_[1].setOnElementRemoved((key) => {
            let resourceKey = this.tileset_[1].resourcePath_ + key;
            let tile = this.resourceCache_.findResourceByName(resourceKey);
            if (tile) {
                tile.vertexBuffer_.deleteVertexBuffer();
                tile.indexBuffer_.deleteBuffer();
                this.resourceCache_.removeResource(resourceKey);
            }

            //console.log(this.resourceCache_.findResourceByName(this.tileset_[1].resourcePath_
            // + key)); console.log(this.lruCache_[1]); this.lruCache_[1];
        });
        /*
         worker.postMessage({
         question:
         'The Answer to the Ultimate Question of Life, The Universe, and Everything.',
         });
         worker.onmessage = ({ data: { answer } }) => {
         console.log(answer);
         };
         */

        this.readBackPixelX_ = 0;
        this.readBackPixelY_ = 0;

        this.voxelSelectionPoint_ = vec3.create();

        // event handling
        window.addEventListener("keydown", (event) => {
            this.getKeyboardEventHandler().keyDown(event);
        });
        window.addEventListener("keyup", (event) => {
            this.getKeyboardEventHandler().keyUp(event);
        });

        canvas.addEventListener("mousedown", (event) => {
            this.getMouseEventHandler().onMouseDown(event);
        });
        canvas.addEventListener("mouseup", (event) => {
            this.getMouseEventHandler().onMouseUp(event);
        });
        canvas.addEventListener("mousemove", (event) => {
            this.getMouseEventHandler().onMouseMove(event);
        });
        canvas.addEventListener("mouseleave", (event) => {
            this.getMouseEventHandler().onMouseLeave(event);
        });
        canvas.addEventListener("click", (event) => {
            this.getMouseEventHandler().onMouseClick(event);
        });
        canvas.addEventListener("dblclick", (event) => {
            this.getMouseEventHandler().onMouseDoubleClick(event);
        });
        canvas.addEventListener("wheel", (event) => {
            event.preventDefault();
            this.getMouseEventHandler().onMouseWheel(event);
        });
        canvas.addEventListener(
            "touchstart",
            (event) => {
                this.getTouchEventHandler().onTouchStart(event);
            },
            hasPassive ? { passive: false } : false
        );
        canvas.addEventListener(
            "touchend",
            (event) => {
                this.getTouchEventHandler().onTouchEnd(event);
            },
            hasPassive ? { passive: false } : false
        );
        canvas.addEventListener(
            "touchcancel",
            (event) => {
                this.getTouchEventHandler().onTouchCancel(event);
            },
            hasPassive ? { passive: false } : false
        );
        canvas.addEventListener(
            "touchmove",
            (event) => {
                this.getTouchEventHandler().onTouchMove(event);
            },
            hasPassive ? { passive: false } : false
        );

        canvas.addEventListener(
            "contextmenu",
            (e) => {
                e.preventDefault();
                this.getMouseEventHandler().onMouseRightClick();
            },
            false
        );

        // Event loop
        this.lastTimeStamp_ = 0;

        this.init().then(() => {
            requestAnimationFrame((timestamp) => {
                this.loop(timestamp);
            });
        });
    }

    loop(timestamp) {
        let deltaTime = timestamp - this.lastTimeStamp_;
        this.update(deltaTime / 100);
        this.render(timestamp);
        this.lastTimeStamp_ = timestamp;
        requestAnimationFrame((ts) => {
            this.loop(ts);
        });
    }

    getKeyboardEventHandler() {
        return this.keyboard_handler_;
    }

    getMouseEventHandler() {
        return this.mouseEventHander_;
    }

    getTouchEventHandler() {
        return this.touchEventHandler_;
    }

    async init() {
        // Load and create resources
        await this.loadImageResources(verdana_png, gFontTextureName);

        await this.loadBmFont();

        //this.VoxelBrickMesher_ = await VoxelBrickMesher();
        //this.voxelBrick_ = new this.VoxelBrickMesher_.VoxelBrick(VoxelDim,
        // VoxelHeight);
        this.voxelModel_ = new VoxelModel(
            this.glContext_,
            this.resourceCache_,
            32,
            64,
            0.2,
            0.003
        );

        this.voxelTransferFunction_ = new VoxelTransferFunction();

        await this.loadVoxelData();
        await this.voxelModel_.init();
        await this.voxelTransferFunction_.loadVoxelTransfer(voxelDefinition);

        const gl = this.glContext_.gl();
        this.voxelTransferTexture_ = new WgTexture(
            "voxelTransferTexture",
            this.glContext_
        );
        this.voxelTransferTexture_.create2D(gl.RGBA16F, 256, 1, true, false);
        this.voxelTransferTexture_.uploadDataToTexture(
            gl.RGBA,
            gl.FLOAT,
            this.voxelTransferFunction_.transferFunctionColors_
        );
        this.resourceCache_.addResource(this.voxelTransferTexture_);

        this.voxelModel_.setTransferTexture(this.voxelTransferTexture_);

        this.loadTileset();

        this.createFbo();

        this.primRender_ = new WgPrimRender("primitiveRender", this.glContext_);
        this.wuxiRender_ = new WgWuxiRender(
            "WuxiRender",
            this.glContext_,
            this.resourceCache_
        );

        this.clipPolygon_.setClosePolygonRadius(0.01 * this.domainSize_[0]);
        this.createUi();

        this.createPrimitives();
        // voxel model
        this.voxelModel_.resetAllVoxelBricks();
    }

    loadTileset() {
        this.tileset_ = [null, null];

        fetch(`${tilesetPath}/tileset.json`, { mode: "cors" }).then((resp) => {
            resp.json().then((json) => {
                this.tileset_[0] = new W3DTileSet();

                this.tileset_[0].load3DTileSet(json.root, tilesetPath);

                this.tileset_[0].filterVisibleLeaves(
                    this.orbitCamera_,
                    this.geoTransform_
                );
            });
        });

        fetch(`${buildingsTilesetPath}/tileset.json`, { mode: "cors" }).then(
            (resp) => {
                resp.json().then((json) => {
                    this.tileset_[1] = new W3DTileSet();

                    this.tileset_[1].load3DTileSet(json.root, buildingsTilesetPath);

                    this.tileset_[1].filterVisibleLeaves(
                        this.orbitCamera_,
                        this.geoTransform_
                    );
                });
            }
        );
    }

    createFbo() {
        const gl = this.glContext_.gl();
        this.readBackFbo_ = new WgFbo("ReadBackFbo", this.glContext_);
        this.readBackTexture_ = new WgTexture("readBackTexture", this.glContext_);
        this.readBackTexture_.create2D(
            gl.RGBA8,
            this.glContext_.getCanvasWidth(),
            this.glContext_.getCanvasHeight(),
            true,
            false
        );
        this.readBackTexture_.activeTexture_ = gl.TEXTURE0;
        this.readBackFbo_.attachTexture(this.readBackTexture_);
        this.readBackFbo_.createDepthStencilBuffer(
            this.glContext_.getCanvasWidth(),
            this.glContext_.getCanvasHeight()
        );
        this.readBackFbo_.unbind();
    }

    createPrimitives() {
        this.sphere_ = new WSphere(vec3.fromValues(100.0, 5.0, 100.0), 8.0);
        this.cylinder1_ = new WCylinder("cyl1", 25.0, 2.0);
        this.cylinder1_.setCenterPosition(vec3.fromValues(150, 10.0, 200.0));

        this.cylinder2_ = new WCylinder("cyl2", 25.0, 2.0);
        this.cylinder2_.setCenterPosition(vec3.fromValues(170, 10.0, 200.0));

        this.cylinder3_ = new WCylinder("cyl3", 25.0, 2.0);
        this.cylinder3_.setCenterPosition(vec3.fromValues(185, 10.0, 210.0));

        this.cylinder4_ = new WCylinder("cyl4", 25.0, 2.0);
        this.cylinder4_.setCenterPosition(vec3.fromValues(185, 10.0, 170.0));

        this.cylinder5_ = new WCylinder("cyl5", 25.0, 2.0);
        this.cylinder5_.setCenterPosition(vec3.fromValues(150, 10.0, 170.0));

        this.cylinder6_ = new WCylinder("cyl5", 25.0, 1.5);
        this.cylinder6_.setCenterPosition(vec3.fromValues(150, 10.0, 155.0));

        //this.box0_ = new WBox("box0", vec3.fromValues(0.0, 0.0, 0.0),
        // vec3.fromValues(256.0, 256.0, drawHeight));
        this.clipPlaneDrawBox_ = new WBox(
            "box0",
            vec3.fromValues(0.0, 0.0, 0.0),
            vec3.fromValues(
                this.voxelModel_.getVoxelDimension()[0],
                this.voxelModel_.getVoxelDimension()[2],
                drawHeight
            )
        );

        let ellipsOrientation = mat3.create();
        mat3.fromRotation(ellipsOrientation, wMathConst.fPI_HALF * 0.9);

        /*   let covMat = mat3.fromValues(
         1.0, 2.0, 0.0,
         2.0, 1.0, 0.0,
         0.0, 0.0, 1.0
         )

         let covMat2 = mat3.fromValues(
         1.25425468, 0.1824987, 0.18482315,
         0.1824987,  0.97253923,  0.07018075,
         0.18482315,  0.07018075,  0.91375323
         )

         let eigenValues = vec3.create();
         let eigenVectors = WMath.calculateEigenValues(covMat2, eigenValues);

         this.ellipsoid0_ = new WEllipsoid("ellipsoid0", vec3.fromValues(-20.0, 20.0, 0.0), vec3.fromValues(17.0, 12.0, 9.0),  eigenVectors);// ellipsOrientation);*/
    }

    createUi() {
        // Wuxi
        this.wuxi_ = new WUxi("Wuxi");
        this.wuxi_.addButton(
            0,
            "Button1",
            vec3.fromValues(-10.0, 70.0, 0.0),
            28.0,
            8.0
        );

        this.wuxi_.addButton(
            ToggleClipPlaneDrawButtonId,
            "Toggle clip Button",
            vec3.fromValues(115.0, 80.0, 20.0),
            50.0,
            16.0
        );
        this.wuxi_.addTextLabel(
            ToggleClipPlaneLabelId,
            "textLabel1",
            vec3.fromValues(115.0, 95.0, 20.0),
            50.0,
            10.0,
            "Toggle draw clipplane mode",
            this.textLayout_
        );

        this.wuxi_.addButton(
            ToggleAxisAlignedClipPlaneId,
            "ToggleAxisClipplanes",
            vec3.fromValues(170.0, 80.0, 20.0),
            50.0,
            16.0
        );
        this.wuxi_.addTextLabel(
            AxisClipPlaneToggleAxisLabelId,
            "textLabel2",
            vec3.fromValues(170.0, 95.0, 20.0),
            50.0,
            10.0,
            "Toggle axis clipplanes",
            this.textLayout_
        );
        this.wuxi_.addTextLabel(
            AxisClipPlaneLabelId,
            "axisClipLabel",
            vec3.fromValues(this.domainSize_[0], this.domainSize_[1], 2),
            2.0,
            2.0,
            "X",
            this.textLayout_
        );

        this.wuxi_.addFrame(ClipPlaneFrameId, "ClipPlaneFrame");

        // Clipping frame
        let clipPlaneFrame = this.wuxi_.findUiElement(ClipPlaneFrameId);
        //clipPlaneFrame.addWidget(this.wuxi_.findUiElement(ToggleClipPlaneDrawButtonId));
        // clipPlaneFrame.addWidget(this.wuxi_.findUiElement(ToggleClipPlaneLabelId));
        // clipPlaneFrame.addWidget(this.wuxi_.findUiElement(ToggleAxisAlignedClipPlaneId));
        // clipPlaneFrame.addWidget(this.wuxi_.findUiElement(AxisClipPlaneToggleAxisLabelId));

        clipPlaneFrame.addWidget(this.wuxi_.findUiElement(AxisClipPlaneLabelId));
        clipPlaneFrame.setColor(vec4.fromValues(1.0, 0.5, 0.0, 0.2));
        this.wuxi_.findUiElement(ClipPlaneFrameId).setFrameBorder(0.3);

        this.wuxi_.addClipPlane(
            clipPlanePosXIndex,
            "Xpos",
            vec3.fromValues(0.0, this.domainSize_[1] * 0.5, 0.0),
            10,
            this.domainSize_[0],
            wClipPlaneAxis.X_POS_AXIS
        );
        this.wuxi_.addClipPlane(
            clipPlanePosZIndex,
            "Zpos",
            vec3.fromValues(this.domainSize_[0] * 0.5, this.domainSize_[1], 0.0),
            this.domainSize_[1],
            10,
            wClipPlaneAxis.Y_POS_AXIS
        );
        // picking frame
        this.wuxi_.addFrame(PickingInfoFrameId, "PickingIdFrame");
        let pickingFrame = this.wuxi_.findUiElement(PickingInfoFrameId);
        pickingFrame.setFrameBorder(0.3);
        this.wuxi_.addTextLabel(
            PickingNameLabel_,
            "PickingNameLabel",
            vec3.fromValues(0.0, 14.0, 0.0),
            4.0,
            0.8,
            PickingNameStr_ + "None",
            this.textLayout_
        );
        this.wuxi_.addTextLabel(
            PickingIdLabel_,
            "PickingIdLabel",
            vec3.fromValues(0.0, 15.0, 0.0),
            4.0,
            0.8,
            PickingIdStr_ + "-1",
            this.textLayout_
        );

        pickingFrame.addWidget(this.wuxi_.findUiElement(PickingNameLabel_));
        pickingFrame.addWidget(this.wuxi_.findUiElement(PickingIdLabel_));
        this.wuxi_.findUiElement(PickingInfoFrameId).setHidden();

        this.wuxi_.findUiElement(clipPlanePosXIndex).setHidden();
        this.wuxi_.findUiElement(clipPlanePosZIndex).setHidden();

        this.wuxi_.findUiElement(0).setColor(vec4.fromValues(1.0, 0.3, 0.3, 0.8));
        this.wuxi_.findUiElement(0).registerButtonClickCallback(buttonCallBack);
        this.wuxi_.findUiElement(0).setHidden();

        this.wuxi_
            .findUiElement(ToggleClipPlaneDrawButtonId)
            .registerButtonClickCallback(buttonCallBack);
        this.wuxi_
            .findUiElement(ToggleAxisAlignedClipPlaneId)
            .registerButtonClickCallback(buttonCallBack);

        this.wuxi_.findUiElement(ToggleClipPlaneLabelId).setHidden();
        this.wuxi_.findUiElement(AxisClipPlaneLabelId).setHidden();
        this.wuxi_.findUiElement(ToggleClipPlaneDrawButtonId).setHidden();
        this.wuxi_.findUiElement(ToggleAxisAlignedClipPlaneId).setHidden();
        this.wuxi_.findUiElement(AxisClipPlaneToggleAxisLabelId).setHidden();
    }

    createTextureFromImage(image, textureName, createMipMap) {
        const texture_res = new WgTexture(textureName, this.glContext_);
        texture_res.create2dFromImage(
            image,
            this.glContext_.gl().RGBA,
            this.glContext_.gl().RGBA,
            this.glContext_.gl().UNSIGNED_BYTE,
            false,
            createMipMap
        );
        this.resourceCache_.addResource(texture_res);
    }

    async loadImageResources(url, name) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                this.createTextureFromImage(img, name, true);
                resolve(img);
            };
            img.onerror = () => {
                reject(new Error(`Could not load image ${url}`));
            };
            img.crossOrigin = "Anonymous";
            img.src = url;
        });
    }

    async loadBmFont() {
        const response = await fetch(verdana_json);
        this.fontInfo_ = await response.json();
        this.textLayout_ = new WTextLayout(this.fontInfo_);
        const fontTexture =
            this.resourceCache_.findResourceByName(gFontTextureName);
        this.fontRender_ = new WgFontRender(
            "FontRender",
            this.glContext_,
            this.textLayout_,
            fontTexture
        );
    }

    async loadVoxelData() {
        // let voxels = new Int32Array(voxelDim * voxelDim * voxelHeight);
        let resp = await fetch(aarhus_nrrd);
        let buf = await resp.arrayBuffer();
        let nrrdData = nrrd.parse(buf);
        const triScale = nrrdData.spaceDirections[0][0] / 1000.0; // 1 gl unit is
        // 1km
        // domainSize used to scale positions to uv
        this.domainSize_[0] = nrrdData.sizes[0] * triScale;
        this.domainSize_[1] = nrrdData.sizes[1] * triScale;
        vec3.copy(this.domainOrigin_, nrrdData.spaceOrigin);
        this.voxelModel_.setVoxelData(
            nrrdData.sizes[0],
            nrrdData.sizes[2],
            nrrdData.sizes[1],
            nrrdData.data
        );

        this.voxelModel_.setHeightOffset(0.001 * nrrdData.spaceOrigin[2]);
        this.voxelModel_.setHeightScale(this.heightScale_);

        //mat4.set(this.geoTransform_,  1/1000, 0, 0, 0, 0, 1/1000, 0, 0, 0,  0,
        // 1/1000, 0, -nrrdData.spaceOrigin[0]/1000, -nrrdData.spaceOrigin[1]/1000,
        // -nrrdData.spaceOrigin[2]/1000.0, 1);
        mat4.set(
            this.geoTransform_,
            1 / 1000,
            0,
            0,
            0,
            0,
            1 / 1000,
            0,
            0,
            0,
            0,
            1 / 1000,
            0,
            -nrrdData.spaceOrigin[0] / 1000,
            -nrrdData.spaceOrigin[1] / 1000,
            0.0,
            1
        );

        let bb = [
            this.domainOrigin_[0],
            this.domainOrigin_[1],
            this.domainOrigin_[0] + 1000 * this.domainSize_[0],
            this.domainOrigin_[1] + 1000 * this.domainSize_[1],
        ];

        try {
            await this.loadImageResources(
                aarhus_overlay_image_url + bb.join(","),
                "overlay",
                true
            );
        } catch (error) {
            console.log("error");
        }


        this.voxelModel_.setOverlayTexture(
            this.resourceCache_.findResourceByName("overlay")
        );
    }

    updateWuxi(isLeftMouseDown, cameraHasFocus) {
        // wuxi ui update
        if (!this.wuxi_) {
            return;
        }
        this.wuxi_.updateUiElements(
            this.orbitCamera_.getView(),
            this.orbitCamera_.getRight()
        );
        let uiElements = this.wuxi_.getUiElements();
        let updateHover = !cameraHasFocus;
        let wuxiElementActive = false;
        const R = this.orbitCamera_.getRight();
        const U = this.orbitCamera_.getUp();

        for (let i = 0; i < uiElements.length; i++) {
            let uiElement = uiElements[i];

            if (uiElement.getIsHidden()) {
                continue;
            }

            if (updateHover) {
                uiElement.updateState(this.pickingRay_, isLeftMouseDown);
            } else {
                uiElement.resetHover();
            }

            if (uiElement.getState().hover_) {
                updateHover = false;
            }

            if (uiElement.getIsActive()) {
                wuxiElementActive = true;
            }

            if (!uiElement.hasParent()) {
                uiElement.updateOrientation(R, U);
            } else {
                uiElement.updateFromParent();
            }
        }
        return wuxiElementActive;
    }

    /*
     this.cameraActiveCounter_--;
     let cameraHasFocus = this.cameraActiveCounter_ > 0;

     let fx = this.getMouseEventHandler().getNormX();
     let fy = this.getMouseEventHandler().getNormY();
     this.pickingRay_ = this.orbitCamera_.getPickingRay(fx, fy);
     let isLeftMouseDown = this.getMouseEventHandler().isDown(wMouseButtons.LeftButton);
     */

    updateAxisClipPlane() {
        if (!this.wuxi_) {
            return;
        }
        let clipX = this.wuxi_.findUiElement(clipPlanePosXIndex);
        let clipZ = this.wuxi_.findUiElement(clipPlanePosZIndex);
        // update axis aligned clip planes
        if (this.currentAxisClipPlane_ != currentClipPlaneAxis_) {
            this.currentAxisClipPlane_ = currentClipPlaneAxis_;

            if (this.currentAxisClipPlane_ == 0) {
                this.wuxi_.findUiElement(clipPlanePosXIndex).setVisible();
                this.wuxi_.findUiElement(clipPlanePosZIndex).setHidden();
                this.wuxi_.findUiElement(AxisClipPlaneLabelId).setLabelText("X");
                this.wuxi_.findUiElement(AxisClipPlaneLabelId).setVisible();
                this.wuxi_
                    .findUiElement(ClipPlaneFrameId)
                    .setPosition(
                        vec3.fromValues(
                            clipX.getClipPosition(),
                            0.5 * this.domainSize_[1],
                            10
                        )
                    );
                this.wuxi_
                    .findUiElement(AxisClipPlaneLabelId)
                    .setColor(vec4.fromValues(1.0, 0.2, 0.2, 0.8));
            }

            if (this.currentAxisClipPlane_ == 1) {
                this.wuxi_.findUiElement(clipPlanePosXIndex).setHidden();
                this.wuxi_.findUiElement(clipPlanePosZIndex).setVisible();
                this.wuxi_.findUiElement(AxisClipPlaneLabelId).setLabelText("Y");
                this.wuxi_.findUiElement(AxisClipPlaneLabelId).setVisible();
                this.wuxi_
                    .findUiElement(ClipPlaneFrameId)
                    .setPosition(
                        vec3.fromValues(
                            0.5 * this.domainSize_[0],
                            clipZ.getClipPosition(),
                            10
                        )
                    );
                this.wuxi_
                    .findUiElement(AxisClipPlaneLabelId)
                    .setColor(vec4.fromValues(0.0, 0.2, 1.0, 0.8));
            }

            if (this.currentAxisClipPlane_ == 2) {
                this.wuxi_.findUiElement(clipPlanePosXIndex).setHidden();
                this.wuxi_.findUiElement(clipPlanePosZIndex).setHidden();
                this.wuxi_.findUiElement(AxisClipPlaneLabelId).setHidden();
            }
        }

        if (clipX.getIsActive() && !clipX.getIsHidden()) {
            this.voxelModel_.clipPlanes_.clipXMin_ =
                (clipX.getClipPosition() * this.voxelModel_.voxelDimension_[0]) /
                this.domainSize_[0];
            this.voxelModel_.updateClipPlane();
            this.wuxi_
                .findUiElement(ClipPlaneFrameId)
                .setPosition(
                    vec3.fromValues(
                        clipX.getClipPosition(),
                        0.5 * this.domainSize_[1],
                        10
                    )
                );

            this.scheduleRender_ = true;
        }

        if (clipZ.getIsActive() && !clipZ.getIsHidden()) {
            this.wuxi_
                .findUiElement(ClipPlaneFrameId)
                .setPosition(
                    vec3.fromValues(
                        0.5 * this.domainSize_[0],
                        clipZ.getClipPosition(),
                        10
                    )
                );
            this.voxelModel_.clipPlanes_.clipZMin_ =
                this.voxelModel_.voxelDimension_[2] -
                (clipZ.getClipPosition() * this.voxelModel_.voxelDimension_[2]) /
                this.domainSize_[1];
            this.voxelModel_.updateClipPlane();
            this.scheduleRender_ = true;
        }
    }

    updateClipPolygon(cameraHasFocus) {
        // user clip plane drawing
        if (this.appMode_ == AppMode.AppDrawClipPlane && !cameraHasFocus) {
            let hitinfo = this.clipPlaneDrawBox_.getIntersection(this.pickingRay_);

            if (hitinfo.validHit_) {
                let isecPos;
                isecPos = this.pickingRay_.evaluateRay(hitinfo.hitTimes_[0]);
                vec3.copy(this.boxIsecPoint_, isecPos);
                //isecPos[0] = (isecPos[0]); // snap to voxel
                //isecPos[1] = (isecPos[1]);
                isecPos[2] += 0.001;
            }

            if (this.getMouseEventHandler().isClicked(wMouseButtons.LeftButton)) {
                if (hitinfo.validHit_ && !this.clipPolygon_.isClosed()) {
                    if (this.clipPolygon_.isClosed()) {
                        this.clipPolygon_.clear();
                    }
                    this.clipPolygon_.addPoint(
                        vec2.fromValues(
                            Math.round(this.boxIsecPoint_[0]) - 0.001,
                            Math.round(this.boxIsecPoint_[1]) - 0.001
                        )
                    );

                    if (this.clipPolygon_.isClosed()) {
                        if (this.clipPolygon_.isValid()) {
                            // Create a clip polygon in voxel space
                            let scaledClipPolygon = new ClipPolygon();
                            const voxelDim = this.voxelModel_.getVoxelDimension();
                            for (let i = 0; i < this.clipPolygon_.getNumberOfPoints(); i++) {
                                let point = this.clipPolygon_.getPoint(
                                    this.clipPolygon_.getNumberOfPoints() - 1 - i
                                );

                                point[0] = Math.round(
                                    (point[0] * voxelDim[0]) / this.domainSize_[0]
                                );
                                point[1] = Math.round(
                                    voxelDim[2] -
                                    1 -
                                    (point[1] * voxelDim[2]) / this.domainSize_[1]
                                );
                                scaledClipPolygon.addPoint(point);
                            }
                            this.voxelModel_.updateClipPolygon(scaledClipPolygon);
                        } else {
                            this.voxelModel_.resetAllVoxelBricks();
                        }

                        this.appMode_ = AppMode.AppNormal;
                    }
                }
            }
        }
    }

    handleWuxiEvents() {
        if (buttonEvent_) {
            buttonEvent_ = false;
            this.voxelModel_.resetAllVoxelBricks();
        }

        if (toggleDrawClipPlaneEvent_) {
            toggleDrawClipPlaneEvent_ = false;
            if (this.appMode_ == AppMode.AppNormal) {
                this.appMode_ = AppMode.AppDrawClipPlane;
                this.wuxi_
                    .findUiElement(ToggleClipPlaneDrawButtonId)
                    .setColor(vec4.fromValues(0.3, 0.6, 0.3, 0.5));
                this.clipPolygon_.clear();
                this.voxelModel_.resetAllVoxelBricks();

                this.wuxi_.findUiElement(clipPlanePosXIndex).setHidden();
                this.wuxi_.findUiElement(clipPlanePosZIndex).setHidden();
                this.wuxi_.findUiElement(AxisClipPlaneLabelId).setHidden();
            } else {
                this.appMode_ = AppMode.AppNormal;
                this.wuxi_
                    .findUiElement(ToggleClipPlaneDrawButtonId)
                    .setColor(vec4.fromValues(0.3, 0.3, 0.3, 0.75));
            }
        }
    }

    update(deltaTime) {
        this.cameraActiveCounter_--;
        const cameraHasFocus = this.cameraActiveCounter_ > 0;
        const isLeftMouseDown = this.getMouseEventHandler().isDown(
            wMouseButtons.LeftButton
        );

        this.pickingRay_ = this.orbitCamera_.getPickingRay(
            this.getMouseEventHandler().getNormX(),
            this.getMouseEventHandler().getNormY()
        );

        this.voxelLightValue_ +=
            this.appMode_ == AppMode.AppDrawClipPlane ? -0.01 : 0.01;
        this.voxelLightValue_ = WMath.clampF(this.voxelLightValue_, 0.75, 1.0);

        if (this.appMode_ == AppMode.AppDrawClipPlane) {
            this.updateClipPolygon(cameraHasFocus);
            this.scheduleRender_ = true;
        } else {
            this.updateAxisClipPlane();
        }

        this.handleWuxiEvents();

        let wuxiElementActive = this.updateWuxi(isLeftMouseDown, cameraHasFocus);
        if (wuxiElementActive) {
            this.scheduleRender_ = true;
        }

        if (this.voxelModel_.update()) {
            this.scheduleRender_ = true;
        }

        // in case of no wuxi interaction we can update the camera
        if (!wuxiElementActive) {
            if (
                this.orbitCamera_.update(
                    deltaTime,
                    this.getMouseEventHandler(),
                    this.getKeyboardEventHandler(),
                    this.getTouchEventHandler()
                )
            ) {
                this.voxelModel_.updateVoxelBrickDepths(this.orbitCamera_.getView());

                this.cameraActiveCounter_ = 4;
                this.scheduleRender_ = true;
                // Update frustum to follow camera
                this.frustum_.updateFromViewProjection(
                    this.orbitCamera_.getViewProjection()
                );
                this.frustum_.updateFrustumPoints(
                    this.orbitCamera_.getViewProjection()
                );
                for (let k = 0; k < this.tileset_.length; k++) {
                    if (this.tileset_[k] && this.showTileset_[k]) {
                        this.visibleTiles_[k] = this.tileset_[k].filterVisibleLeaves(
                            this.orbitCamera_,
                            this.geoTransform_,
                            this.tilesMaxDistance_
                        ).hitNodes;
                    }
                }
            }
        }

        this.visibleTiles_.forEach((visibleTiles, j) => {
            if (!this.showTileset_[j]) {
                return;
            }
            for (let i = 0; i < visibleTiles.length; i++) {
                const node = visibleTiles[i];

                if (!this.lruCache_[j].inCache(node.content.uri)) {
                    let cachedTile = this.loadTile(node, j);
                    if (cachedTile) {
                        this.lruCache_[j].insert(node.content.uri, cachedTile);
                    } else {
                        // Insert dummy object for future replacement.
                        this.lruCache_[j].insert(node.content.uri, {});
                    }
                    break;
                } else {
                    this.lruCache_[j].update(visibleTiles[i].content.uri);
                }
            }
        });

        //this.scheduleRender_ = true;

        /*  if (this.getKeyboardEventHandler().isPressed('p')) {
         PerformPickingPass = true;
         console.log("picking");
         }*/

        if (this.getMouseEventHandler().isDoubleClicked(wMouseButtons.LeftButton)) {
            PerformPickingPass = true;
        }

        if (PerformPickingPass) {
            this.readBackPixelX_ = this.getMouseEventHandler().clientX_;
            this.readBackPixelY_ =
                this.glContext_.getCanvasHeight() -
                1 -
                this.getMouseEventHandler().clientY_;

            if (
                this.readBackPixelX_ < 0 ||
                this.readBackPixelX_ >= this.glContext_.getCanvasWidth()
            ) {
                console.log("invalid mouse x readback pos:" + this.readBackPixelX_);
                PerformPickingPass = false;
            }

            if (
                this.readBackPixelY_ < 0 ||
                this.readBackPixelY_ >= this.glContext_.getCanvasHeight()
            ) {
                console.log("invalid mouse y readback pos:" + this.readBackPixelY_);
                PerformPickingPass = false;
            }
            this.scheduleRender_ = true; // Make sure to schedule at new render
            // with picking has taken place
        }

        this.getMouseEventHandler().flush();
        this.getTouchEventHandler().flush();
    }

    updatePrimRender() {
        /*
         this.primRender_.clearTextures();
         this.primRender_.addGridXZ(10, 51.2)
         this.primRender_.addFrustumWireframe(this.frustum_.frustumPoints_, wVec4Colors.Orange);

         this.primRender_.addSphere(this.sphere_, wVec4Colors.Green);
         this.primRender_.addCylinder(this.cylinder1_, wVec4Colors.Grey);
         this.primRender_.addCylinder(this.cylinder2_, wVec4Colors.LightGrey);
         this.primRender_.addCylinder(this.cylinder3_, wVec4Colors.Yellow);
         this.primRender_.addCylinder(this.cylinder4_, wVec4Colors.Orange);
         this.primRender_.addCylinder(this.cylinder5_, wVec4Colors.Red);
         this.primRender_.addCylinder(this.cylinder6_, wVec4Colors.Orange);
         this.primRender_.addTexture2D(0.0, 0.4, 0.01, 0.02, this.voxelTransferTexture_);
         // this.primRender_.addTexture2D(0.8, 1.0, 0.8, 1.0, this.readBackFbo_.getAttachment(0));

         /* let ellipsoidVerts = this.ellipsoid0_.getTriangulation();
         for (let i = 0; i < ellipsoidVerts.length; i += 3) {

         this.primRender_.addTriangle(
         WMath.vec4FromVec3(ellipsoidVerts[i]), wVec4Colors.Red,
         WMath.vec4FromVec3(ellipsoidVerts[i + 1]), wVec4Colors.Green,
         WMath.vec4FromVec3(ellipsoidVerts[i + 2]), wVec4Colors.Blue,
         );
         }
         */
        let voxelInfoFrame = this.wuxi_.findUiElement(PickingInfoFrameId);

        if (!voxelInfoFrame.getIsHidden()) {
            let frameBottom = WMath.vec3Sub(
                voxelInfoFrame.getCenterPosition(),
                vec3.fromValues(0.0, 0.0, voxelInfoFrame.getHeight() * 0.51)
            );
            let toFrame = WMath.vec3Sub(frameBottom, this.voxelSelectionPoint_);
            let lineEnd = WMath.vec3Add(this.voxelSelectionPoint_, toFrame);
            this.primRender_.clearPrimitive();
            this.primRender_.addLine(
                WMath.vec4FromVec3(this.voxelSelectionPoint_),
                wVec4Colors.White,
                WMath.vec4FromVec3(lineEnd),
                wVec4Colors.LightGrey
            );
        }

        this.primRender_.addTexture2D(
            0.0,
            0.4,
            0.01,
            0.04,
            this.voxelTransferTexture_
        );

        if (this.appMode_ == AppMode.AppDrawClipPlane) {
            this.primRender_.clearPrimitive();

            for (let i = 0; i < this.clipPolygon_.getNumberOfPoints(); i++) {
                let cp0;
                let cp1;

                if (i == 0 && !this.clipPolygon_.isClosed()) {
                    let initialPoint = this.clipPolygon_.getPoint(i);
                    const p = vec4.fromValues(
                        initialPoint[0],
                        initialPoint[1],
                        drawHeight,
                        1.0
                    );
                    cp1 = vec2.fromValues(this.boxIsecPoint_[0], this.boxIsecPoint_[1]);

                    const cirlceColor = this.clipPolygon_.isPointInsideClosingCircle(cp1)
                        ? wVec4Colors.Green
                        : wVec4Colors.White;
                    this.primRender_.addCircleWireFrameXY(
                        p,
                        this.clipPolygon_.getClosePolygonRadius(),
                        cirlceColor
                    );
                }

                if (
                    !this.clipPolygon_.isClosed() &&
                    i == this.clipPolygon_.getNumberOfPoints() - 1
                ) {
                    cp0 = this.clipPolygon_.getPoint(i);
                    cp1 = vec2.fromValues(this.boxIsecPoint_[0], this.boxIsecPoint_[1]);
                } else {
                    cp0 = this.clipPolygon_.getPoint(i);
                    cp1 = this.clipPolygon_.getPoint(
                        (i + 1) % this.clipPolygon_.getNumberOfPoints()
                    );
                }

                const v0 = vec4.fromValues(cp0[0], cp0[1], drawHeight, 1.0);
                const v1 = vec4.fromValues(cp1[0], cp1[1], drawHeight, 1.0);

                if (i < this.clipPolygon_.getNumberOfPoints() - 1) {
                    let polyNormal = this.clipPolygon_.getNormal(i);
                    const midPoint = vec4.fromValues(
                        cp0[0] + (cp1[0] - cp0[0]) * 0.5,
                        cp0[1] + (cp1[1] - cp0[1]) * 0.5,
                        drawHeight,
                        1.0
                    );
                    let normLen =
                        0.5 * (this.domainSize_[0] + this.domainSize_[1]) * 0.01;
                    const norm = vec4.fromValues(
                        midPoint[0] + polyNormal[0] * normLen,
                        midPoint[1] + polyNormal[1] * normLen,
                        drawHeight,
                        1.0
                    );
                    this.primRender_.addLine(
                        midPoint,
                        wVec4Colors.Green,
                        norm,
                        wVec4Colors.Yellow
                    );
                }

                this.primRender_.addLine(v0, wVec4Colors.White, v1, wVec4Colors.White);
                this.primRender_.addPoint(v0, wVec4Colors.Red);
            }
        }
    }

    updateWuxiRender() {
        if (!this.wuxiRender_) {
            return;
        }

        // render update back to front by reversing the list
        this.wuxiRender_.clearElements();
        let uiElements = this.wuxi_.getUiElements();
        for (let i = uiElements.length - 1; i >= 0; i--) {
            let uiElement = uiElements[i];
            if (!uiElement.getIsHidden()) {
                this.wuxiRender_.addUiElement(uiElement);
            }
        }
    }

    loadTile(node, tilesetIndex) {
        if (!node.content) {
            return;
        }

        if (
            this.resourceCache_.findResourceByName(
                this.tileset_[tilesetIndex].resourcePath_ + node.content.uri
            )
        ) {
            return this.resourceCache_.findResourceByName(
                this.tileset_[tilesetIndex].resourcePath_ + node.content.uri
            );
        } else {
            let wtile = new W3DTile(node, this.tileset_[tilesetIndex].resourcePath_);
            wtile.tilesetIndex_ = tilesetIndex;
            this.tileLoaderWorker_.postMessage(wtile);
            return null;
            //this.tileLoaderWorker_.onmessage(wtile);
            //await wtile.loadB3dmTile(this.tileset_.resourcePath_ +
            // node.content.uri); this.resourceCache_.addResource(wtile); return
            // wtile;
        }
    }

    renderPickingFbo(vp, v) {
        const gl = this.gl;
        gl.clearColor(0.0, 0.0, 0.0, 1.0);

        this.readBackFbo_.bind();
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        this.boreholeIdShader_.bindProgram();

        this.boreholeIdShader_.unbindProgram();
        this.voxelModel_.render(
            vp,
            v,
            this.orbitCamera_.getDir(),
            this.voxelLightValue_,
            true
        );
        const pixels = new Uint8Array(4);
        gl.readPixels(
            this.readBackPixelX_,
            this.readBackPixelY_,
            1,
            1,
            gl.RGBA,
            gl.UNSIGNED_BYTE,
            pixels
        );
        this.readBackFbo_.unbind();

        PerformPickingPass = false;

        if (pixels[3] > 0 && pixels[0] > 0) {
            let idLabel = this.wuxi_.findUiElement(PickingIdLabel_);
            let voxelNameLabel = this.wuxi_.findUiElement(PickingNameLabel_);
            idLabel.setLabelText(PickingIdStr_ + pixels[0]);
            //console.log("pixel readback values:" + pixels[0] + "," + pixels[1] +
            // "," + pixels[2] + "," + pixels[3]);

            let voxelName = this.voxelTransferFunction_.getVoxelName(pixels[0]);
            voxelNameLabel.setLabelText(PickingNameStr_ + voxelName);

            let voxelInfoFrame = this.wuxi_.findUiElement(PickingInfoFrameId);
            this.voxelSelectionPoint_ = this.pickingRay_.evaluateRay(pixels[2] * 5.0);
            let isecPos = this.pickingRay_.evaluateRay(
                Math.max(1.0, pixels[2] * 5.0)
            );
            isecPos[2] = Math.max(isecPos[2], 3.35);
            voxelInfoFrame.setFrameCenterPosition(isecPos);
            voxelInfoFrame.setVisible();

            //this.orbitCamera_.setOrbitPivotTarget(this.voxelSelectionPoint_);
        } else {
            this.wuxi_.findUiElement(PickingInfoFrameId).setHidden();
        }
    }

    renderTile(cache, node, shader) {
        if (!cache.inCache(node.content.uri)) {
            return false;
        }
        const tile = cache.cache_[node.content.uri].value_;
        if (!tile.vertexBuffer_) {
            return false;
        }

        const gl = this.gl;
        //console.log(this.lruCache_.cache_[node.content.uri].value_);
        //tile.indexBuffer_.bind();
        let mvp = mat4.create();
        let transform = mat4.create();
        mat4.fromScaling(transform, [0.001, 0.001, 0.001 * this.heightScale_]);
        transform[12] = tile.transform_[12] * 0.001 + this.geoTransform_[12];
        transform[13] = -tile.transform_[14] * 0.001 + this.geoTransform_[13];

        mat4.multiply(mvp, this.orbitCamera_.getViewProjection(), transform);

        let transparency = 0.0;
        let blendOnsetFraction = 0.8;
        let blendEnabled = false;
        if (node.distance_ > blendOnsetFraction * this.tilesMaxDistance_) {
            gl.enable(gl.BLEND);
            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            transparency =
                (node.distance_ - blendOnsetFraction * this.tilesMaxDistance_) /
                ((1.0 - blendOnsetFraction) * this.tilesMaxDistance_);
            blendEnabled = true;
        }
        shader.setUniformMat4fv("VP", mvp);
        shader.setUniform1f("transparency", transparency);

        tile.vertexBuffer_.bind();
        gl.drawElements(
            gl.TRIANGLES,
            tile.indexBuffer_.numberOfElements_,
            gl.UNSIGNED_INT,
            0
        );
        tile.vertexBuffer_.unbind();

        if (blendEnabled) {
            gl.disable(gl.BLEND);
        }

        return true;
    }

    render() {
        const vp = this.orbitCamera_.getViewProjection();
        const v = this.orbitCamera_.getView();
        if (!DISABLE_PICKING) {
            if (PerformPickingPass) {
                this.renderPickingFbo(vp, v);
                this.updateWuxi(false, false);
                this.scheduleRender_ = true;
            }
        }

        if (!this.scheduleRender_) {
            return;
        }
        this.updateWuxiRender();
        const gl = this.glContext_.gl();
        this.updateFps();

        this.updatePrimRender();

        gl.clearColor(0.3, 0.3, 0.3, 1.0);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        gl.enable(gl.DEPTH_TEST);

        // actual rendering

        let renderedTiles = [0, 0];
        let shaders = [this.boreholeShader_, this.buildingsShader_];
        for (let k = 0; k < this.visibleTiles_.length; k++) {
            if (this.showTileset_[k]) {
                let shader = shaders[k];

                shader.bindProgram();

                for (let i = 0; i < this.visibleTiles_[k].length; i++) {
                    const node = this.visibleTiles_[k][i];

                    let tileRendered = this.renderTile(this.lruCache_[k], node, shader);
                    if (tileRendered) {
                        renderedTiles[k]++;
                    }

                    if (renderedTiles[k] > 64) {
                        break;
                    }
                }
                shader.unbindProgram();
            }

            if (k == 0) {
                //const mvp = mat4.create();
                //mat4.multiply(mvp, vp, [0.2,0,0,0 , 0,0,0.1,0, 0,-0.066,0,0,
                // 0,100,-51.2,1  ] ); this.voxelModel_.setHeightScale(1);
                // this.voxelModel_.setHeightOffset(0);

                //this.voxelModel_.render(vp, this.orbitCamera_.getDir(),
                // this.voxelLightValue_);
                this.voxelModel_.render(
                    vp,
                    v,
                    this.orbitCamera_.getDir(),
                    this.voxelLightValue_,
                    false
                );
            }
        }

        this.appInfo_.visibleTilesBorehole = renderedTiles[0];
        this.appInfo_.visibleTilesBuildings = renderedTiles[1];

        // debug
        let clipPlaneController = this.wuxi_.findUiElement(clipPlanePosXIndex);
        //  this.primRender_.addWLine(clipPlaneController.getClipOrthoLine(),wVec4Colors.Green,wVec4Colors.Yellow);
        // this.primRender_.addPoint(clipPlaneController.orthoLinePoint_,
        // wVec4Colors.Yellow );
        // this.primRender_.addPoint(clipPlaneController.lineProjectedPoint_,
        // wVec4Colors.Red );

        this.primRender_.render(vp);

        //this.voxelModel_.render(vp, this.orbitCamera_.getDir(),
        // this.voxelLightValue_);

        this.wuxiRender_.render(
            vp,
            this.orbitCamera_.getRight(),
            this.orbitCamera_.getUp()
        );
        this.fontRender_.render();

        //  var glErr = this.gl.getError();
        this.scheduleRender_ = false;
    }

    updateFps() {
        this.frameCount_++;
        let timeSinceLastFpsMeasure = (performance.now() - this.lastFps_) / 1000.0;
        if (timeSinceLastFpsMeasure > 1.0) {
            let fps = this.frameCount_ / timeSinceLastFpsMeasure;
            this.lastFps_ = performance.now();
            this.frameCount_ = 0;
            this.appInfo_.fps = Math.round(fps);
        }
    }

    resize(width, height) {
        this.glContext_.canvas_.width = width;
        this.glContext_.canvas_.height = height;
        this.orbitCamera_.setViewport(
            this.glContext_.getCanvasWidth(),
            this.glContext_.getCanvasHeight()
        );
        this.orbitCamera_.setReversedZPerspective(60.0);
        if (this.readBackFbo_) {
            this.readBackFbo_.resize(width, height);
        }
        this.glContext_.gl_.viewport(0, 0, width, height);
        if (this.fontRender_) {
            this.fontRender_.resize();
        }
    }

    setHeightScale(scale) {
        if (scale != this.heightScale_) {
            this.heightScale_ = Math.max(1, scale);
            if (this.voxelModel_) {
                this.voxelModel_.setHeightScale(this.heightScale_);
            }
            this.scheduleRender_ = true;
        }
    }

    getHeightScale() {
        return this.heightScale_;
    }

    setBuildingsEnabled(enabled) {
        this.showTileset_[1] = enabled;
        this.scheduleRender_ = true;
    }

    setBoreholesEnabled(enabled) {
        this.showTileset_[0] = enabled;
        this.scheduleRender_ = true;
    }

    setShowOverlayTexture(show) {
        if (this.voxelModel_) {
            if (show == false) {
                this.voxelModel_.setOverlayTexture(null);
            } else {
                this.voxelModel_.setOverlayTexture(
                    this.resourceCache_.findResourceByName("overlay")
                );
            }
            this.scheduleRender_ = true;
        }
    }

    setClipPolygonEnabled(enabled) {
        if (this.voxelModel_) {
            this.voxelModel_.resetAllVoxelBricks();
        }
        this.clipPolygon_.clear();
        if (enabled) {
            this.appMode_ = AppMode.AppDrawClipPlane;
        } else {
            this.appMode_ = AppMode.AppNormal;
        }
        this.scheduleRender_ = true;
    }

    setClipAxis(axisId) {
        currentClipPlaneAxis_ = axisId;
        this.updateAxisClipPlane();
        this.updateWuxiRender();
        this.scheduleRender_ = true;
    }
}
